import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '@app/environment/environment';
import { isInvestOpen } from '@app/helpers/project.helper';
import { Investment, InvestmentStatus } from '@app/models/investment.models';
import { MainImage, Project } from '@app/models/project.models';

import { PeriodDurationPipe } from '@app/pipes/period-duration.pipe';
import { ThreeDecimalsPipe } from '@app/pipes/three-decimals.pipe';
import { ProjectService } from '@app/services/projects/service';
import { ToastService } from '@app/services/toast.service';
import { AuthStore } from '@app/stores/auth.store';
import { ProjectsStore } from '@app/stores/projects/projects.store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, map, Observable, Observer, Subscription, switchMap } from 'rxjs';
import { ProjectDisplayType } from '../../project-vertical-card/project-vertical-card.component';

@Component({
  selector: 'app-project-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PeriodDurationPipe,
    ThreeDecimalsPipe,
  ],
  templateUrl: './project-card.component.html',
  styleUrl: './project-card.component.scss',
})
export class ProjectCardComponent implements OnInit, OnDestroy {
  public project = input.required<Project>();
  public displayType = input<ProjectDisplayType>('public');
  public investment = input<Investment | undefined>(undefined);
  public detailsButtonFullWidth = input<boolean>(false);
  public incomingOffersCount = input<number>(0);

  public imageSrc: SafeUrl | null = null;

  public onDetailsClicked = output<string>();
  public onIncomingOffersClicked = output<string>();
  public onPublicDetailsClicked = output<string>();
  public onEditWizardClicked = output<string>();
  public pendingInvestmentCancelled = output<void>();

  protected showDetailsButton = computed(() => {
    switch (this.displayType()) {
      case 'myRunningProjects':
      case 'underPreparation':
      case 'followedUnderPreparation':
        return false;
      default:
        return true;
    }
  });

  protected isFundraisingCard = computed(
    () => this.displayType() === 'myRunningProjects',
  );

  protected isInvestmentDisplayType = computed(() => {
    switch (this.displayType()) {
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'investments':
        return true;
      default:
        return false;
    }
  });

  protected showFollow = computed(() => {
    switch (this.displayType()) {
      case 'public':
      case 'myProjects':
      case 'myRunningProjects':
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'investments':
      case 'followed':
      case 'followedUnderPreparation':
        return this.authStore.isLoggedIn();
      case 'admin':
      case 'closed':
      case 'underPreparation':
      case 'myUnderPreparationProjects':
      case 'myUnderVerificationProjects':
        return false;
    }
    return false;
  });

  protected isInvestOpen = computed(() => {
    const p = this.project();
    return isInvestOpen(p.state, p.startCollectionDate, p.endCollectionDate);
  });

  protected projectsStore = inject(ProjectsStore);

  private imageSubscription: Subscription | null = null;

  private authStore = inject(AuthStore);
  private projectService = inject(ProjectService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private cancelInvestmentInFlight = false;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {
    effect((onCleanup) => {
      const until = this.investment()?.pendingUntil;
      onCleanup(() => {
        if (this.countdownIntervalId != null) {
          clearInterval(this.countdownIntervalId);
          this.countdownIntervalId = null;
        }
      });
      if (!until) return;
      this.countdownIntervalId = setInterval(() => {
        this.cdr.markForCheck();
      }, 1000);
    });
  }

  ngOnInit(): void {
    const images = this.getImages();
    if (images.length > 0) {
      const firstImage = images[0];

      this.imageSubscription?.unsubscribe();

      this.imageSubscription = this.getImageUrl(firstImage.id).subscribe({
        next: (safeUrl) => {
          this.imageSrc = safeUrl;
        },
        error: (error) => {
          console.error('Error loading image:', error);
          this.imageSrc = null;
        },
      });
    } else {
      this.imageSrc = null;
    }
  }

  ngOnDestroy(): void {
    this.imageSubscription?.unsubscribe();
  }

  getImages(): MainImage[] {
    const mainImages = this.project()?.mainImages;
    return (
      mainImages?.filter((image) => image.contentType?.includes('image')) ?? []
    );
  }

  getDateString(date: string): string {
    return new Date(date).toLocaleDateString();
  }

  protected getGoalProgress(): number | null {
    const p = this.project();
    const target = p?.targetAmount;
    const current = p?.currentAmount;
    if (target == null || target <= 0 || current == null) return null;
    return (current / target) * 100;
  }

  protected getShowGoalSection(): boolean {
    const target = this.project()?.targetAmount;
    return target != null && target > 0;
  }

  protected getGoalTarget(): number {
    return this.project()?.targetAmount ?? 0;
  }

  protected getGoalCurrent(): number {
    return this.project()?.currentAmount ?? 0;
  }

  copyToClipboard(value: string): void {
    if (!value?.trim()) return;
    void navigator.clipboard.writeText(value.trim());
  }

  getPendingUntilCountdown(pendingUntil: string | null): string {
    if (!pendingUntil) return '';
    const end = new Date(pendingUntil).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.floor((end - now) / 1000));
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  protected cancelPendingInvestment(): void {
    const id = this.investment()?.id;
    if (
      !id ||
      this.displayType() !== 'pendingInvestments' ||
      this.cancelInvestmentInFlight
    ) {
      return;
    }
    this.cancelInvestmentInFlight = true;
    this.projectService
      .cancelInvestment(id)
      .pipe(finalize(() => (this.cancelInvestmentInFlight = false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('projectCard.investment.cancelSuccess'),
          );
          this.pendingInvestmentCancelled.emit();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('projectCard.investment.cancelFailed'),
          );
        },
      });
  }

  protected hasDisplayAccountOwner(): boolean {
    return !!this.investment()?.accountOwner?.trim();
  }

  protected hasDisplayAccountNumber(): boolean {
    return !!this.investment()?.accountNumber?.trim();
  }

  protected hasDisplayComment(): boolean {
    const investment = this.investment();
    return !!(investment?.identifier?.trim() || investment?.comment?.trim());
  }

  protected getDisplayAccountOwner(): string {
    return this.investment()?.accountOwner?.trim() ?? '';
  }

  protected getDisplayAccountNumber(): string {
    return this.investment()?.accountNumber?.trim() ?? '';
  }

  protected getDisplayComment(): string {
    const investment = this.investment();
    const identifier = investment?.identifier?.trim();
    const comment = investment?.comment?.trim();
    return identifier || comment || '';
  }

  protected isActiveFundedInvestment(): boolean {
    return this.investment()?.status === InvestmentStatus.ActiveFunded;
  }

  protected getIncomingOffersCountText(): string {
    const count = this.incomingOffersCount();
    return Number.isFinite(count) && count > 0 ? String(count) : '0';
  }

  protected hasIncomingOffers(): boolean {
    const count = this.incomingOffersCount();
    return Number.isFinite(count) && count > 0;
  }

  follow(projectId: string): void {
    this.projectService.follow(projectId).subscribe({
      next: () => {
        this.projectsStore.addFollowedProject(this.project() as Project);
      },
    });
  }

  unFollow(projectId: string): void {
    this.projectService.unFollow(projectId).subscribe({
      next: () => {
        this.projectsStore.removeFollowedProject(projectId);
      },
    });
  }

  private getImageUrl(imageId: string): Observable<SafeUrl | null> {
    const url = `${environment.apiUrl}/File/${imageId}`;

    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap((blob) => this.createDataUrlObservable(blob)),
      map((dataUrl) => this.sanitizer.bypassSecurityTrustUrl(dataUrl)),
    );
  }

  private createDataUrlObservable(blob: Blob): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
      reader.readAsDataURL(blob);

      return () => {};
    });
  }
}
